import * as React from 'react'

import { cn } from '@/lib/utils'

interface ProgressProps extends React.ComponentProps<'div'> {
  /** Completion from 0 to 100. Values outside the range are clamped. */
  value?: number
  /** Tailwind classes for the filled portion (defaults to the primary colour). */
  indicatorClassName?: string
  /** When false, the fill tracks `value` immediately (for rAF-driven progress). Default true. */
  animate?: boolean
}

/**
 * Minimal, dependency-free progress bar. A filled inner bar is translated by the remaining percentage,
 * which the browser can hardware-accelerate.
 */
function Progress({
  value = 0,
  className,
  indicatorClassName,
  animate = true,
  ...props
}: ProgressProps) {
  const clamped = Math.min(100, Math.max(0, value))

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      className={cn('relative h-2 w-full overflow-hidden rounded-full bg-muted', className)}
      {...props}
    >
      <div
        className={cn(
          'h-full w-full flex-1 bg-primary',
          animate && 'transition-transform duration-500',
          indicatorClassName,
        )}
        style={{ transform: `translateX(-${100 - clamped}%)` }}
      />
    </div>
  )
}

export { Progress }
